import type {
  ResolveWidgetVehicleResponse,
  WidgetBootstrapResponse,
  WidgetMotVehicleAnswer,
  WidgetVehicleTypeResponse,
} from '../types/PublicBookingChannelTypes'
import { isMotService } from './jobTypes'
import type { BasketItem } from './services'

/**
 * "What is it?" (ACA-110).
 *
 * Without HaynesPro the registration lookup often cannot tell the MOT class:
 * DVSA says "Ford Transit" or "Honda CBR", not how heavy the van is or how big
 * the bike. The server then prices the MOT as a car's (class 4), so a 125cc
 * bike and a 3.5 tonne van were both quoted the class 4 fee.
 *
 * So when the class is unknown and an MOT test is in the basket, the widget
 * asks one question, with nothing chosen: the server prices nothing as a car
 * by default any more, so the MOT has no price and the customer cannot go on
 * until they answer. Each answer is sent to the server, which quotes the car
 * again and prices the booking from it. It never overrides a class the lookup
 * knew. A lorry (HGV) gets no MOT at all.
 */

export interface MotVehicleOption {
  value: WidgetMotVehicleAnswer
  label: string
}

export const MOT_VEHICLE_QUESTION = 'What is it?'
export const MOT_VEHICLE_HINT = 'So we can price your MOT.'
export const MOT_VEHICLE_ERROR = "We couldn't update the price for that. Please try again."
export const MOT_HGV_MESSAGE = "We can't MOT this vehicle (HGV)"

export const MOT_VEHICLE_OPTIONS: readonly MotVehicleOption[] = [
  { value: 'CAR_OR_LIGHT_VAN', label: 'Car, or van up to 3,000kg' },
  { value: 'VAN_OVER_3000KG', label: 'Van over 3,000kg' },
  { value: 'HGV', label: 'Lorry or goods vehicle over 3,500kg' },
  { value: 'MOTORCYCLE_UP_TO_200CC', label: 'Motorbike up to 200cc' },
  { value: 'MOTORCYCLE_OVER_200CC', label: 'Motorbike over 200cc' },
  { value: 'THREE_WHEELER', label: 'Three-wheeler' },
  { value: 'MINIBUS', label: 'Minibus (13+ seats)' },
]

/** True when the lookup, or the answer, says this is an HGV: no MOT is offered. */
export function isHgv(vehicle: ResolveWidgetVehicleResponse | null): boolean {
  return vehicle?.motUnavailableReason === 'HGV'
}

/** The same plate, however it was typed. */
export function samePlate(a: string | null | undefined, b: string | null | undefined): boolean {
  const plate = (value: string | null | undefined) => (value ?? '').replace(/\s+/g, '').toUpperCase()
  return plate(a) !== '' && plate(a) === plate(b)
}

/** True when the basket holds an MOT test (see isMotService). */
export function basketHasMotTest(
  basket: BasketItem[],
  services: WidgetBootstrapResponse.ServiceItem[]
): boolean {
  const mots = new Set(services.filter(isMotService).map((service) => service.id))
  return basket.some((item) => mots.has(item.serviceId))
}

/**
 * Whether to ask "What is it?".
 *
 * Only for a lookup that quoted the car (a backend that knows the question),
 * could not tell its class and did not find a lorry, and only while the basket
 * has an MOT test or the customer has already answered: an answer can take the
 * MOT out (a garage that doesn't test bikes), and they must still be able to
 * change it back.
 *
 * @param lookup the registration lookup's own answer, before any answer here
 */
export function shouldAskVehicleType(
  lookup: ResolveWidgetVehicleResponse | null,
  hasMotTest: boolean,
  answer: WidgetMotVehicleAnswer | null
): boolean {
  if (!lookup || !lookup.quotes || lookup.motClass != null || isHgv(lookup)) return false
  return hasMotTest || answer != null
}

/**
 * The lookup with the server's answer to "What is it?" laid over it: its MOT
 * class, whether the garage tests it, whether it is a lorry, and the quotes. The lookup itself when
 * there is no answer for this plate.
 */
export function withVehicleTypeAnswer(
  lookup: ResolveWidgetVehicleResponse | null,
  answered: WidgetVehicleTypeResponse | null
): ResolveWidgetVehicleResponse | null {
  if (!lookup || !answered || !samePlate(lookup.vehicleReg, answered.vehicleReg)) return lookup
  return {
    ...lookup,
    motClass: answered.motClass ?? lookup.motClass,
    motClassTested: answered.motClassTested ?? lookup.motClassTested,
    motUnavailableReason: answered.motUnavailableReason ?? null,
    quotes: answered.quotes ?? lookup.quotes,
    evServicesUnavailable: answered.evServicesUnavailable ?? lookup.evServicesUnavailable,
  }
}
